import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Collapse,
  Button,
  Modal,
  DatePicker,
  Select,
  Space,
  Empty,
  Spin,
} from "antd";
import { LoadingOutlined, FilterOutlined } from "@ant-design/icons";
import { _get_support_staffs } from "../../../common/axios_services";
import AllOrders from "../../support/components/AllOrders";
import OrderDetailsModal from "../../support/components/OrderDetailsModal";

const { Option } = Select;

const ProgressContentBase = ({
  useOwnLayout = false,
  showFilter = true,
  filterType = "date", // "date" or "team"
  externalData = null,
  externalLoading = false,
  externalFetchData = null,
  externalFetchOrders = null,
  externalOrders = [],
  externalPagination = { page: 1, limit: 10, total: 0 },
  externalGetDefaultDates = null,
  userRole = "SO",
}) => {
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [selectedDates, setSelectedDates] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // Current tab for orders

  const [selectedSupportManager, setSelectedSupportManager] = useState(null);
  const [selectedSupportOfficer, setSelectedSupportOfficer] = useState(null);
  const [supportManagers, setSupportManagers] = useState([]);
  const [supportOfficers, setSupportOfficers] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const displayData = externalData;
  const isLoading = externalLoading;

  const getDefaultDates = () => {
    if (externalGetDefaultDates) {
      return externalGetDefaultDates();
    }
    // Default: last 7 days
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    return {
      dateFrom: weekAgo.toISOString().split("T")[0],
      dateTo: today.toISOString().split("T")[0],
    };
  };

  const fetchSupportManagers = async () => {
    try {
      setLoadingTeams(true);
      const response = await _get_support_staffs({
        role: "support_manager",
        limit: 100,
      });
      const managers =
        response?.data?.data?.data || response?.data?.data || [];
      setSupportManagers(Array.isArray(managers) ? managers : []);
    } catch (error) {
      console.error("Error fetching support managers:", error);
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchSupportOfficers = async (supportManagerId) => {
    try {
      setLoadingTeams(true);
      const response = await _get_support_staffs({
        role: "support_officer",
        limit: 100,
      });
      const staffData =
        response?.data?.data?.data || response?.data?.data || [];
      if (Array.isArray(staffData)) {
        let officers = staffData;
        if (supportManagerId) {
          officers = officers.filter(
            (so) =>
              so.supportManagerId === supportManagerId ||
              so.managerId === supportManagerId,
          );
        }
        setSupportOfficers(officers);
      } else {
        setSupportOfficers([]);
      }
    } catch (error) {
      console.error("Error fetching support officers:", error);
    } finally {
      setLoadingTeams(false);
    }
  };

  useEffect(() => {
    if (filterModalVisible && filterType === "team") {
      fetchSupportManagers();
      fetchSupportOfficers(null);
    }
  }, [filterModalVisible, filterType]);

  useEffect(() => {
    if (externalFetchOrders) {
      externalFetchOrders(activeTab, 1, 10, selectedSupportManager, selectedSupportOfficer);
    }
  }, [externalFetchOrders]);

  const handleSupportManagerChange = (value) => {
    setSelectedSupportManager(value);
    setSelectedSupportOfficer(null);
    fetchSupportOfficers(value);
  };

  const handleApplyFilter = () => {
    if (filterType === "date" && selectedDates) {
      const [dateFrom, dateTo] = selectedDates;
      if (externalFetchData) {
        externalFetchData(
          dateFrom.format("YYYY-MM-DD"),
          dateTo.format("YYYY-MM-DD"),
        );
      }
      if (externalFetchOrders) {
        externalFetchOrders(
          "all",
          1,
          100,
          selectedSupportManager,
          selectedSupportOfficer,
        );
      }
      setFilterModalVisible(false);
    } else if (filterType === "team") {
      if (externalFetchData) {
        externalFetchData(
          null,
          null,
          selectedSupportManager,
          selectedSupportOfficer,
        );
      }
      if (externalFetchOrders) {
        externalFetchOrders(
          "all",
          1,
          100,
          selectedSupportManager,
          selectedSupportOfficer,
        );
      }
      setFilterModalVisible(false);
    }
  };

  const handleClearFilter = () => {
    if (filterType === "date") {
      const defaultDates = getDefaultDates();
      if (externalFetchData) {
        externalFetchData(defaultDates.dateFrom, defaultDates.dateTo);
      }
      if (externalFetchOrders) {
        externalFetchOrders("all", 1, 100, null, null);
      }
      setSelectedDates(null);
      setFilterModalVisible(false);
    } else if (filterType === "team") {
      setSelectedSupportManager(null);
      setSelectedSupportOfficer(null);
      if (externalFetchData) {
        externalFetchData(null, null, null, null);
      }
      if (externalFetchOrders) {
        externalFetchOrders("all", 1, 100, null, null);
      }
      setFilterModalVisible(false);
    }
  };

  const restaurants = displayData?.restaurants || [];

  const renderRestaurantItems = () => {
    if (restaurants.length === 0) {
      return [];
    }

    return restaurants.map((restaurant, idx) => ({
      key: idx,
      label: (
        <p style={{ display: "inline" }}>
          {restaurant.restaurantName} - Waiting:{" "}
          {restaurant.stats?.waiting?.count || 0} | Accept:{" "}
          {restaurant.stats?.accept?.count || 0} | Ready:{" "}
          {restaurant.stats?.ready?.count || 0} | Pickup:{" "}
          {restaurant.stats?.pickup?.count || 0} | In-Transit:{" "}
          {restaurant.stats?.inTransit?.count || 0} | Delivered:{" "}
          {restaurant.stats?.delivered?.count || 0}
        </p>
      ),
      children: (
        <div>
          <ul className="mobile-list-flex">
            <li>
              <span>Restaurant:</span>
              <span>{restaurant.restaurantName}</span>
            </li>
            <li>
              <span>Waiting:</span>
              <span>
                {restaurant.stats?.waiting?.count || 0} (
                {restaurant.stats?.waiting?.percentage || 0}%)
              </span>
            </li>
            <li>
              <span>Accepted:</span>
              <span>
                {restaurant.stats?.accept?.count || 0} (
                {restaurant.stats?.accept?.percentage || 0}%)
              </span>
            </li>
            <li>
              <span>Ready:</span>
              <span>
                {restaurant.stats?.ready?.count || 0} (
                {restaurant.stats?.ready?.percentage || 0}%)
              </span>
            </li>
            <li>
              <span>Pickup:</span>
              <span>
                {restaurant.stats?.pickup?.count || 0} (
                {restaurant.stats?.pickup?.percentage || 0}%)
              </span>
            </li>
            <li>
              <span>In-Transit:</span>
              <span>
                {restaurant.stats?.inTransit?.count || 0} (
                {restaurant.stats?.inTransit?.percentage || 0}%)
              </span>
            </li>
            <li>
              <span>Delivered:</span>
              <span>
                {restaurant.stats?.delivered?.count || 0} (
                {restaurant.stats?.delivered?.percentage || 0}%)
              </span>
            </li>
            <li>
              <span>Total:</span>
              <span>{restaurant.stats?.total || 0}</span>
            </li>
          </ul>
        </div>
      ),
    }));
  };

  const inProgressOrders = useMemo(() => {
    return (externalOrders || []).filter((order) => {
      const status = order.status || order.order?.status;
      const isPickup = order.deliveryType === "PICKUP" || order.order?.deliveryType === "PICKUP";
      const isCompleted = status === "DELIVERED" || status === "RETURNED" || status === "REJECTED" || status === "CANCELLED" || (status === "PICKED" && isPickup);
      return !isCompleted;
    });
  }, [externalOrders]);

  const completedOrders = useMemo(() => {
    return (externalOrders || []).filter((order) => {
      const status = order.status || order.order?.status;
      const isPickup = order.deliveryType === "PICKUP" || order.order?.deliveryType === "PICKUP";
      const isCompleted = status === "DELIVERED" || status === "RETURNED" || status === "REJECTED" || status === "CANCELLED" || (status === "PICKED" && isPickup);
      return isCompleted;
    });
  }, [externalOrders]);

  return (
    <Spin spinning={isLoading} indicator={antIcon}>
      <div className={`${useOwnLayout ? "pb-40" : ""} h-screen overflow-y-auto`}>
        <div className="main_container_title_div mb-5 mt-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Order Progress
          </h1>
          <p className="text-gray-600 text-xl sm:text-base">
            Track order status across your restaurants
          </p>
        </div>

        {showFilter && (
          <div className="mb-4">
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterModalVisible(true)}
              type="default"
            >
              {filterType === "team" ? "Filter by Team" : "Filter by Date"}
            </Button>
          </div>
        )}

        <Card className="mb-6" bordered={false}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Waiting</p>
              <p className="text-2xl font-bold text-gray-900">
                {displayData?.stats?.waiting?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {displayData?.stats?.waiting?.percentage || 0}%
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Accept</p>
              <p className="text-2xl font-bold text-gray-900">
                {displayData?.stats?.accept?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {displayData?.stats?.accept?.percentage || 0}%
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Ready</p>
              <p className="text-2xl font-bold text-gray-900">
                {displayData?.stats?.ready?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {displayData?.stats?.ready?.percentage || 0}%
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Pickup</p>
              <p className="text-2xl font-bold text-gray-900">
                {displayData?.stats?.pickup?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {displayData?.stats?.pickup?.percentage || 0}%
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">In-Transit</p>
              <p className="text-2xl font-bold text-gray-900">
                {displayData?.stats?.inTransit?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {displayData?.stats?.inTransit?.percentage || 0}%
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded">
              <p className="text-xs text-gray-600">Delivered</p>
              <p className="text-2xl font-bold text-gray-900">
                {displayData?.stats?.delivered?.count || 0}
              </p>
              <p className="text-xs text-gray-500">
                {displayData?.stats?.delivered?.percentage || 0}%
              </p>
            </div>
          </div>
        </Card>

        <Card className="mb-6" bordered={false}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Live Orders (Real-time Progress)</h3>
          </div>
          <AllOrders
            orders={inProgressOrders}
            loading={externalLoading}
            onViewOrder={(order) => {
              setSelectedOrder({ ...order, readOnly: true });
              setIsOrderModalVisible(true);
            }}
            onRefresh={() => {
              if (externalFetchOrders) {
                externalFetchOrders(activeTab, 1, 10, selectedSupportManager, selectedSupportOfficer);
              }
            }}
          />
        </Card>

        <Card className="mb-6" bordered={false}>
          <h3 className="text-lg font-semibold mb-4">COMPLETED ORDER</h3>
          <div className="mt-4">
            <AllOrders
              orders={completedOrders}
              loading={externalLoading}
              onViewOrder={(order) => {
                setSelectedOrder({ ...order, readOnly: true });
                setIsOrderModalVisible(true);
              }}
              onRefresh={() => {
                if (externalFetchOrders) {
                  externalFetchOrders(activeTab, 1, 10, selectedSupportManager, selectedSupportOfficer);
                }
              }}
            />
          </div>
        </Card>
      </div>

      <Modal
        title={
          filterType === "team"
            ? "Filter by Team Member"
            : "Filter by Date Range"
        }
        open={filterModalVisible}
        onOk={handleApplyFilter}
        onCancel={() => setFilterModalVisible(false)}
        okText="Apply"
        cancelText="Close"
        width={filterType === "team" ? 500 : 400}
      >
        <Spin spinning={loadingTeams}>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {filterType === "date" ? (
              <DatePicker.RangePicker
                value={selectedDates}
                onChange={setSelectedDates}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            ) : (
              <>
                {userRole === "SE" && (
                  <div>
                    <label className="block mb-2 font-medium">
                      Support Manager
                    </label>
                    <Select
                      placeholder="Select Support Manager"
                      style={{ width: "100%" }}
                      allowClear
                      value={selectedSupportManager}
                      onChange={handleSupportManagerChange}
                      loading={loadingTeams}
                    >
                      {supportManagers.map((sm) => (
                        <Option key={sm.id} value={sm.id}>
                          {sm.name || sm.firstName || sm.email || sm.id}
                        </Option>
                      ))}
                    </Select>
                  </div>
                )}
                <div>
                  <label className="block mb-2 font-medium">
                    Support Officer
                  </label>
                  <Select
                    placeholder="Select Support Officer"
                    style={{ width: "100%" }}
                    allowClear
                    value={selectedSupportOfficer}
                    onChange={setSelectedSupportOfficer}
                    loading={loadingTeams}
                    disabled={
                      userRole === "SE" && !selectedSupportManager && supportManagers.length > 0
                    }
                  >
                    {supportOfficers.map((so) => (
                      <Option key={so.id} value={so.id}>
                        {so.name || so.firstName || so.email || so.id}
                      </Option>
                    ))}
                  </Select>
                </div>
                <p className="text-sm text-gray-500">
                  Leave empty to view all data
                </p>
              </>
            )}
            <Button block type="default" onClick={handleClearFilter}>
              {filterType === "team"
                ? "Clear Filters (View All)"
                : "Clear Filters (Reset to Today)"}
            </Button>
          </Space>
        </Spin>
      </Modal>

      <OrderDetailsModal
        visible={isOrderModalVisible}
        order={selectedOrder}
        onCancel={() => {
          setIsOrderModalVisible(false);
          setSelectedOrder(null);
        }}
        onIntervention={null} // Read only
      />
    </Spin>
  );
};

export default ProgressContentBase;
